import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";

type IconType = "map-pin" | "clock" | "break";

type ActivityIcon =
  | {
      type: "icon";
      iconType: IconType;
    }
  | {
      type: "image";
      src: string;
      alt?: string;
    };

export interface ActivityItem {
  id: string;
  icon: ActivityIcon;
  title: string;
  subtitle?: string;
  timestamp: string;
}

interface ActivityListProps {
  activities: ActivityItem[];
}

export function ActivityList(props: ActivityListProps) {
  const { activities } = props;

  return (
    <Stack gap={7} sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          left: 14,
          top: 30,
          bottom: 0,
          width: 2,
          borderLeft: (theme) => `2px dashed ${theme.palette.grey[400]}`,
          zIndex: 0,
          pointerEvents: "none",
          opacity: 0.8,
        }}
      />

      {activities.map((activity) => (
        <Stack key={activity.id} direction="row" gap={2} alignItems="center" width="100%">
          <Box
            data-testid={`activity-icon-${
              activity.icon.type === "icon"
                ? `${activity.icon.iconType}-${activity.id}`
                : activity.id
            }`}
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: (theme) => theme.palette.common.white,
              position: "relative",
              zIndex: 1,
              overflow: "hidden",
            }}
          >
            {activity.icon.type === "icon" ? (
              <CbhIcon size="small" type={activity.icon.iconType} />
            ) : (
              <Box
                component="img"
                src={activity.icon.src}
                alt={activity.icon.alt ?? activity.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </Box>

          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Text bold variant="body2">
              {activity.title}
            </Text>
            {activity.subtitle && (
              <Text
                variant="caption"
                data-testid={`activity-${activity.id}-subtitle`}
                sx={{ color: (theme) => theme.palette.grey[700] }}
              >
                {activity.subtitle}
              </Text>
            )}
          </Stack>

          {isDefined(activity.timestamp) && (
            <Text semibold variant="body2" sx={{ flexShrink: 0, marginLeft: 2 }}>
              {activity.timestamp}
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
